import React, { useContext, useEffect, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import classNames from 'classnames';
import { SegmentationContext } from '@/contexts/SegmentationContext';
import {
	getGetBasketQuantityQueryKey,
	getGetBasketQueryKey,
	useRemoveFromBasket,
	useUpdateLine,
} from '@/generated/basket';
import { Area, PurchaseLineResponse, ResourceLocation } from '@/generated/data.schemas';
import { useGetTranslations } from '@/generated/translations';
import { useNotificationStore } from '@/store/notificationStore';
import { AlertTypes } from '../Alert';
import { Button } from '../Button';
import { Icon } from '../Icon';
import { Image } from '../Image';
import { InputText } from '../InputText';
import { Link } from '../Link';
import { Price } from '../Price';
import { TextBadge } from '../TextBadge';
import styles from './BasketLine.module.scss';
import { DiscountModifier } from './DiscountModifier';

export interface DefaultBasketLineProps {
	line: PurchaseLineResponse;
	isFull?: boolean;
	isQuantityReadOnly?: boolean;
	isLinkDisabled?: boolean;
	isRemoveDisabled?: boolean;
	isRemoveQuantityDisabled?: boolean;
	variant?: 'default' | 'applicationLine' | 'additional';
	label?: string;
	enableDiscount?: boolean;
}

const isQuantityInputValid = (value: string): boolean => /^\d+/.test(value);

export const DefaultBasketLine: React.FunctionComponent<DefaultBasketLineProps> = ({
	line,
	isFull,
	variant,
	label,
	isQuantityReadOnly,
	isLinkDisabled,
	isRemoveDisabled,
	enableDiscount,
}) => {
	const {
		id,
		productMasterId,
		productVariantId,
		productNumber,
		image,
		name,
		url,
		quantity,
		price,
		customerDiscount,
		salesRepDiscount,
	} = line;

	const { segmentationId } = useContext(SegmentationContext);
	const { data: translations } = useGetTranslations(segmentationId);

	// We need to keep track of the input value since we perform optimistic updates on quantity
	const [quantityInputValue, setQuantityInputValue] = React.useState(String(quantity));
	const addNotification = useNotificationStore((state) => state.addNotification);
	const queryClient = useQueryClient();

	const productUrl = useMemo(
		() => (productVariantId ? `${url}?variant=${productVariantId}` : url),
		[productVariantId, url],
	);

	const route: ResourceLocation = useMemo(() => {
		return {
			area: Area.Product,
			productMasterId: productMasterId,
			externalRoute: productUrl || '/',
		};
	}, [productMasterId, productUrl]);

	useEffect(() => {
		setQuantityInputValue(String(quantity));
	}, [quantity]);

	const removeFromBasket = useRemoveFromBasket({
		mutation: {
			onSuccess: async (res, variables) => {
				if (res.data.notificationStatus) {
					addNotification({
						children: res.data.notificationStatus,
						type: AlertTypes.DANGER,
					});
				} else {
					await queryClient.invalidateQueries({
						queryKey: getGetBasketQuantityQueryKey(segmentationId),
					});
					// get new total quantity from backend
					await queryClient.setQueryData(getGetBasketQueryKey(variables.segmentationId), res);
				}
			},
			onError: () => {
				addNotification({
					children: translations?.data.shared.genericErrorMsg,
					type: AlertTypes.DANGER,
				});
			},
		},
	});

	const setLineQuantity = useUpdateLine({
		mutation: {
			onSuccess: async (res, variables) => {
				if (res.data.notificationStatus) {
					addNotification({
						children: res.data.notificationStatus,
						type: AlertTypes.DANGER,
					});

					const lines = [
						...(res.data?.lines || []),
						...(res.data?.applicationLines || []),
						...(res.data?.applicationLines?.flatMap((app) => app.additionalBasketLines) || []),
					];
					// Optimistic update might have failed. Updating to committed value...
					setQuantityInputValue((value) => {
						const savedLine = lines.find((updatedLine) => updatedLine.id === id);
						return String(savedLine?.quantity ?? value);
					});
				}

				await queryClient.setQueryData(getGetBasketQueryKey(variables.segmentationId), res);
				// get new total quantity from backend
				await queryClient.invalidateQueries({
					queryKey: getGetBasketQuantityQueryKey(segmentationId),
				});
			},
			onError: () => {
				addNotification({
					children: translations?.data.shared.genericErrorMsg,
					type: AlertTypes.DANGER,
				});

				// Optimistic update failed. Reverting to committed value...
				setQuantityInputValue(String(quantity));
			},
		},
	});

	const mutateQuantity = React.useCallback(
		(newQuantity: number): void => {
			setLineQuantity.mutate({ segmentationId, lineId: id, data: { quantity: newQuantity } });
		},
		[id, segmentationId, setLineQuantity],
	);

	const handleInputChange = React.useCallback((event: React.ChangeEvent<HTMLInputElement>): void => {
		setQuantityInputValue(event.target.value);
	}, []);

	const handleInputBlur = React.useCallback(
		(event: React.FocusEvent<HTMLInputElement>): void => {
			const inputValue = event.target.value;

			// Revert to committed value if invalid
			if (!isQuantityInputValid(inputValue)) {
				setQuantityInputValue(String(quantity));
				return;
			}

			mutateQuantity(Number(event.target.value));
		},
		[quantity, mutateQuantity],
	);

	const handleMinus = React.useCallback((): void => {
		setQuantityInputValue(String(quantity - 1));
		mutateQuantity(quantity - 1);
	}, [quantity, mutateQuantity]);

	const handlePlus = React.useCallback((): void => {
		setQuantityInputValue(String(quantity + 1));
		mutateQuantity(quantity + 1);
	}, [quantity, mutateQuantity]);

	const handleRemoveLine = React.useCallback((): void => {
		removeFromBasket.mutate({ segmentationId, lineId: id });
	}, [id, removeFromBasket, segmentationId]);

	return (
		<div className={styles.lineWrapper}>
			<div
				className={classNames(styles.wrapper, {
					[styles.isLarge]: isFull,
					[styles.isSmall]: !isFull, // quick basket
					[styles.hasNoRemove]: isRemoveDisabled,
					[styles.applicationLine]: variant === 'applicationLine',
						[styles.hasLabel]: !!label,
					})}
			>
				<div className={styles.productField}>
						{label && (
							<div className={styles.labelOverlay}>
								<TextBadge text={label} />
							</div>
						)}
						<div className={styles.imageWrapper}>
						<Image
							className={styles.image}
							size180={image.size180}
							size320={image.size320}
							size480={image.size480}
							size720={image.size720}
							size960={image.size960}
							size1280={image.size1280}
							size1920={image.size1920}
							// Has to match --thumb-size
							responsiveSizesSettings={'56px'}
							altText={image.altText || name}
						/>
					</div>
					<div className={styles.infoWrapper}>
						<Link className={styles.link} route={!isLinkDisabled && productUrl ? route : undefined}>
							<p className={classNames(styles.name, isFull ? 'u-ellipsis-2-lines' : 'u-ellipsis')}>
								{name}
							</p>
							<p className={styles.itemNo}>
								{translations?.data.shared.itemNo} {productNumber}
							</p>
							{isQuantityReadOnly && (
								<p className={styles.quantityText}>
									{translations?.data.basket.quantityTitle}: {quantityInputValue}
								</p>
							)}
						</Link>
						{!isRemoveDisabled && (
							<Button
								className={styles.removeButton}
								variant={'linkButton'}
								size={'sm'}
								aria-label={translations?.data.basket.removeLine || 'Remove item'}
								onClick={handleRemoveLine}
							>
								{translations?.data.basket.removeLine || 'Remove'}
							</Button>
						)}
					</div>
				</div>
				<div className={classNames(styles.field, styles.padding, styles.center, styles.amount)}>
					<div className={styles.quantityWrapper}>
						<Button
							className={styles.responsiveButton}
							variant={'ghost'}
							hasOnlyIcon
							noRadius
							aria-label={translations?.data.basket.removeOneItem || 'Remove one item'}
							disabled={quantity <= 1}
							onClick={handleMinus}
							size={'sm'}
						>
							<Icon name={'minus'} size={'md'} />
						</Button>
						<InputText
							className={styles.quantityInputWrapper}
							inputClassName={styles.quantityInput}
							variant={'inverse'}
							value={quantityInputValue}
							id={String(id)}
							label={translations?.data.basket.quantityTitle}
							name={String(id)}
							type={'number'}
							hideLabel
							onChange={handleInputChange}
							onBlur={handleInputBlur}
							isSmall
						/>
						<Button
							className={styles.responsiveButton}
							variant={'ghost'}
							hasOnlyIcon
							noRadius
							aria-label={translations?.data.basket.addOneItem || 'Add one item'}
							onClick={handlePlus}
							size={'sm'}
						>
							<Icon name={'plus'} size={'md'} />
						</Button>
					</div>
				</div>
				{/* TODO: replace with price value when the price feature is ready */}
				<div className={classNames(styles.field, styles.padding, styles.price, styles.itemPrice)}>
					<div className={styles.priceInfo}>
						<Price price={price} size={!isFull ? 'sm' : 'md'} />
					</div>
				</div>
				<div className={classNames(styles.field, styles.padding, styles.customerDiscount)}>{`${customerDiscount}%`}</div>
				<div className={classNames(styles.field, styles.padding, styles.salesRepDiscount)}>
					{salesRepDiscount > 0 && `${salesRepDiscount}% `}
					{enableDiscount && <DiscountModifier lineId={line.id} />}
				</div>
				{/* TODO: replace with total price value when the price feature is ready */}
				<div className={classNames(styles.field, styles.padding, styles.price)}>
					<div className={styles.priceInfo}>
						<Price price={price} size={!isFull ? 'sm' : 'md'} />
					</div>
				</div>
			</div>
		</div>
	);
};
